import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import { FormFieldLine } from "../../../../../components/form-field-line/form-field-line";
import Card from "../../../../../components/layouts/card/card";
import TableRow from "../../../../../components/table/components/table-row/table-row";
import Table from "../../../../../components/table/table";
import currentPortalStore from "../../../../../stores/current-portal-store";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import DashTranslation from "../../../../../dashboard-translation/dashboard-translaton";
import { Toggle } from "../../../../../components/inputs/toggle/toggle";
import { observer } from "mobx-react";
import {
	INCLUDE_PRODUCT_DUTIES_IN_REFUND,
	INCLUDE_PRODUCT_DUTIES_IN_REFUND_MAP,
	INCLUDE_SHIPPING_COST_IN_REFUND,
	INCLUDE_SHIPPING_COST_IN_REFUND_MAP,
	LINE_ITEMS_REFUND_TRANSACTIONS,
	LINE_ITEMS_REFUND_TRANSACTIONS_MAP,
	REFUND_LEVELS,
	REFUND_LEVELS_MAP,
	REFUND_TYPES,
	REFUNDS_PRIORITY,
	REFUNDS_PRIORITY_MAP
} from "../../../../../models/User";
import { IOption, SelectDropdown } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import { SingleValue } from "react-select";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import { Input } from "../../../../../components/inputs/input/input";
import { PROFILE_CAPABILITIES } from "../../../../../models/ProfileCapabilities";
import ResolveCapability from "../../../../../components/resolve-capability/resolve-capability";
import React from "react";
import { RESOLUTION_TYPE } from "../../../../resolutions/constants";
import DefaultPortalResolutionSelect from "./default-portal-resolution-select";

interface IProps {
    onExpandClick: (expanded: boolean) => void;
    isExpanded: boolean;
    dataName?: string; /** used by Candu to target an element */
}

const RefundsSettingsCard: React.FC<IProps> = observer(({ onExpandClick, isExpanded, dataName }) => {
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-settings-store';

    const storeSetting = currentPortalStore.setting;
    const storeCurrency = currentPortalStore.setting?.currencyCode;

    const refundsPriorityValue = storeSetting?.refundsPriority ? {
        label: getTranslation(REFUNDS_PRIORITY_MAP[storeSetting.refundsPriority].label, pageScope),
        value: storeSetting.refundsPriority
    } : undefined;

    const onChangeRefundsPriorityValue = (value: SingleValue<IOption>) => {
        storeSetting?.setRefundsPriority(value?.value as REFUNDS_PRIORITY);
    }


    const defaultRefundLevelValue = storeSetting?.defaultRefundLevel ? {
        label: getTranslation(REFUND_LEVELS_MAP[storeSetting.defaultRefundLevel].label, pageScope),
        value: storeSetting.defaultRefundLevel
    } : undefined;


    const onChangeDefaultRefundLevelValue = (value: SingleValue<IOption>) => {
        storeSetting?.setDefaultRefundLevel(value?.value as REFUND_LEVELS);
    }

    const onChangeIncludeShippingCostInRefund = (value: SingleValue<IOption>) => {
        storeSetting?.setIncludeShippingCostInRefund(value?.value as INCLUDE_SHIPPING_COST_IN_REFUND);
    }

    const onChangeIncludeProductDutiesInRefund = (value: SingleValue<IOption>) => {
        storeSetting?.setIncludeProductDutiesInRefund(value?.value as INCLUDE_PRODUCT_DUTIES_IN_REFUND);
    }

    const includeShippingCostInRefundValue = storeSetting?.includeShippingCostInRefund ? {
        label: getTranslation(INCLUDE_SHIPPING_COST_IN_REFUND_MAP[storeSetting.includeShippingCostInRefund].label, pageScope),
        value: storeSetting.includeShippingCostInRefund
    } : undefined;

    const includeProductDutiesInRefundValue = storeSetting?.includeProductDutiesInRefund ? {
        label: getTranslation(INCLUDE_PRODUCT_DUTIES_IN_REFUND_MAP[storeSetting.includeProductDutiesInRefund].label, pageScope),
        value: storeSetting.includeProductDutiesInRefund
    } : undefined;

    const isShippingCostRefundTogglesDisabled = (): boolean => {
        return storeSetting?.includeShippingCostInRefund === INCLUDE_SHIPPING_COST_IN_REFUND.YES || storeSetting?.includeShippingCostInRefund === INCLUDE_SHIPPING_COST_IN_REFUND.NO;
    }

    const lineItemRefundTransactionsValue = storeSetting?.lineItemRefundTransactions ? {
        label: getTranslation(LINE_ITEMS_REFUND_TRANSACTIONS_MAP[storeSetting.lineItemRefundTransactions].label, pageScope),
        value: storeSetting.lineItemRefundTransactions
    } : undefined;

    const onChangeLineItemRefundTransactionsValue = (value: SingleValue<IOption>) => {
        storeSetting?.setLineItemRefundTransactions(value?.value as LINE_ITEMS_REFUND_TRANSACTIONS);
    }
    const getShippingCostRefundValue = (refundSettingIsEnabled: boolean): boolean => {
        if (storeSetting?.includeShippingCostInRefund === INCLUDE_SHIPPING_COST_IN_REFUND.YES) {
            return true;
        } else if (storeSetting?.includeShippingCostInRefund === INCLUDE_SHIPPING_COST_IN_REFUND.NO) {
            return false;
        } else {
            return refundSettingIsEnabled;
        }
    }

    const onChangeRefundBonusesLimit = (value: number) => {
        storeSetting?.rmaLevelBonusesLimitStore.setRefundBonusesLimit(+value);
    }

    const onChangeIsRefundBonusActive = (value: boolean) => {
        storeSetting?.rmaLevelBonusesLimitStore.setIsActive(value);
    }

    return (
		<ResolveCapability capability={PROFILE_CAPABILITIES.REFUNDS}>
			{({
				  isAddShippingCostRefundAutomaticallySupported,
				  isAddShippingCostWhenReturningAllItemsSupported,
				  isConfigureWhenShippingCostIsIncludedInRefundsSupported,
				  isLineItemsRefundTransactionsSupported,
				  supportedRefundLevels,
			}) => (
				<Card
					toolbar={{
						title: (
							<FontBody1 variant={FONT_VARIANT.BOLD}>
								<DashTranslation translationKey="REFUNDS" scope={pageScope} />
							</FontBody1>
						)
					}}
					expandCallback={onExpandClick}
					isExpanded={isExpanded}
					dataName={dataName}
				>
					<FormFieldLine
						label={getTranslation('AUTO_POPULATE_REFUND_AMOUNT', pageScope)}
						note={getTranslation('WHEN_ENABLED_REFUND_AMOUNT_AUTOMATICALLY_APPEARS', pageScope)}
						dataName="auto-populate-refund-amount"
					>
						<Toggle
							checked={storeSetting?.autoPopulateRefundAmount}
							onChange={storeSetting?.setAutoPopulateRefundAmount}
							label=''
						/>
					</FormFieldLine>

					{supportedRefundLevels?.length > 1 && (
						<FormFieldLine
							label={getTranslation('DEFAULT_RETURN_LABEL', pageScope)}
							note={getTranslation('DEFINE_YOUR_DEFAULT_REFUND_LEVEL', pageScope)}
							dataName="default-return-label"
						>
							<SelectDropdown
								value={defaultRefundLevelValue}
								onChange={onChangeDefaultRefundLevelValue}
								options={[
									{
										label: getTranslation(REFUND_LEVELS_MAP[REFUND_LEVELS.LINE_ITEM].label, pageScope),
										value: REFUND_LEVELS.LINE_ITEM,
										disabled: !supportedRefundLevels.includes(REFUND_LEVELS.LINE_ITEM),
									},
									{
										label: getTranslation(REFUND_LEVELS_MAP[REFUND_LEVELS.ORDER].label, pageScope),
										value: REFUND_LEVELS.ORDER,
										disabled: !supportedRefundLevels.includes(REFUND_LEVELS.ORDER),
									},
								]}
							/>
						</FormFieldLine>
					)}

					<ResolveCapability capability={PROFILE_CAPABILITIES.REFUND_RESOLUTION}>
						{({ supportedRefundTypes }) => {

							if (
								!supportedRefundTypes.includes(RESOLUTION_TYPE.GiftCard) &&
								!supportedRefundTypes.includes(RESOLUTION_TYPE.Credits)
							) {
								return null;
							}

							return (
								<FormFieldLine
									label={getTranslation('MIXED_REFUNDS_PRIORITY', pageScope)}
									note={getTranslation('SET_THE_PRIORITY_FOR_REFUNDING_PAYMENT_METHODS', pageScope)}
									tooltip={
										<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
											<DashTranslation translationKey="MONEY_CREDIT_CARDS_VIRTUAL_CURRENCY_GIFT_CARD" scope={pageScope} />
										</FontBody2>
									}
									dataName="mixed-refunds-priority"
								>
									<SelectDropdown
										value={refundsPriorityValue}
										onChange={onChangeRefundsPriorityValue}
										options={[
											{
												label: getTranslation(REFUNDS_PRIORITY_MAP[REFUNDS_PRIORITY.MONEY].label, pageScope),
												value: REFUNDS_PRIORITY.MONEY
											},
											{
												label: getTranslation(REFUNDS_PRIORITY_MAP[REFUNDS_PRIORITY.VIRTUAL_CURRENCY].label, pageScope),
												value: REFUNDS_PRIORITY.VIRTUAL_CURRENCY
											},
										]}
									/>
								</FormFieldLine>
							)
						}}
					</ResolveCapability>
					<ResolveCapability capability={PROFILE_CAPABILITIES.DEFAULT_PORTAL_RESOLUTION}>
						{({ isSupported, isFeatureEnabled }) => isSupported && isFeatureEnabled && (
							<FormFieldLine
								label="Default resolution"
								note="When available in the portal, this resolution is pre-selected. Shoppers can still choose another."
								dataName="default-portal-resolution"
							>
								<DefaultPortalResolutionSelect storeSetting={storeSetting} />
							</FormFieldLine>
						)}
					</ResolveCapability>
					{
						(
							// Hide shipping cost title if no shipping cost settings are supported:
							isAddShippingCostRefundAutomaticallySupported ||
							isAddShippingCostWhenReturningAllItemsSupported ||
							isConfigureWhenShippingCostIsIncludedInRefundsSupported
						) && (
							<FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
								{getTranslation('SHOPPING_COST', pageScope)}
							</FontBody2>
						)
					}

					{isAddShippingCostRefundAutomaticallySupported && (
						<FormFieldLine
							label={getTranslation('INCLUDE_SHIPPING_COST_IN_REFUND', pageScope)}
							dataName="include-shipping-cost-in-refund"
						>
							<SelectDropdown
								options={[
									{
										label: getTranslation(INCLUDE_SHIPPING_COST_IN_REFUND_MAP[INCLUDE_SHIPPING_COST_IN_REFUND.YES].label, pageScope),
										value: INCLUDE_SHIPPING_COST_IN_REFUND.YES
									},
									{
										label: getTranslation(INCLUDE_SHIPPING_COST_IN_REFUND_MAP[INCLUDE_SHIPPING_COST_IN_REFUND.NO].label, pageScope),
										value: INCLUDE_SHIPPING_COST_IN_REFUND.NO
									},
									{
										label: getTranslation(INCLUDE_SHIPPING_COST_IN_REFUND_MAP[INCLUDE_SHIPPING_COST_IN_REFUND.CUSTOM].label, pageScope),
										value: INCLUDE_SHIPPING_COST_IN_REFUND.CUSTOM
									},
									{
										label: getTranslation(INCLUDE_SHIPPING_COST_IN_REFUND_MAP[INCLUDE_SHIPPING_COST_IN_REFUND.SET_PER_POLICY_RULE].label, pageScope),
										value: INCLUDE_SHIPPING_COST_IN_REFUND.SET_PER_POLICY_RULE
									}
								]}
								onChange={onChangeIncludeShippingCostInRefund}
								value={includeShippingCostInRefundValue}
							/>
						</FormFieldLine>
					)}
					{storeSetting?.isProductDutiesRefundSupported && (
						<FormFieldLine
							label={getTranslation('INCLUDE_PRODUCT_DUTIES_IN_REFUND', pageScope)}
							note={getTranslation('INCLUDE_PRODUCT_DUTIES_IN_REFUND_DESCRIPTION', pageScope)}
							dataName="include-product-duties-in-refund"
						>
							<SelectDropdown
								options={[
									{
										label: getTranslation(INCLUDE_PRODUCT_DUTIES_IN_REFUND_MAP[INCLUDE_PRODUCT_DUTIES_IN_REFUND.YES].label, pageScope),
										value: INCLUDE_PRODUCT_DUTIES_IN_REFUND.YES
									},
									{
										label: getTranslation(INCLUDE_PRODUCT_DUTIES_IN_REFUND_MAP[INCLUDE_PRODUCT_DUTIES_IN_REFUND.NO].label, pageScope),
										value: INCLUDE_PRODUCT_DUTIES_IN_REFUND.NO
									},
									{
										label: getTranslation(INCLUDE_PRODUCT_DUTIES_IN_REFUND_MAP[INCLUDE_PRODUCT_DUTIES_IN_REFUND.SET_PER_POLICY_RULE].label, pageScope),
										value: INCLUDE_PRODUCT_DUTIES_IN_REFUND.SET_PER_POLICY_RULE
									}
								]}
								onChange={onChangeIncludeProductDutiesInRefund}
								value={includeProductDutiesInRefundValue}
							/>
						</FormFieldLine>
					)}
					{isAddShippingCostWhenReturningAllItemsSupported && (
						<FormFieldLine
							label={getTranslation('INCLUDE_SHIPPING_COST_ALL_ITEMS', pageScope)}
							note={getTranslation('INCLUDE_SHIPPING_COST_ALL_ITEMS_DESCRIPTION', pageScope)}
							dataName="include-shipping-cost-all-items"
						>
							<Toggle
								checked={storeSetting?.includeShippingCostInRefundWhenAllItemsAreReturned}
								onChange={storeSetting?.setIncludeShippingCostInRefundWhenAllItemsAreReturned}
								label=''
							/>
						</FormFieldLine>
					)}
					{isConfigureWhenShippingCostIsIncludedInRefundsSupported && storeSetting?.includeShippingCostInRefund !== INCLUDE_SHIPPING_COST_IN_REFUND.SET_PER_POLICY_RULE && (
						<>
							<FormFieldLine
								label={getTranslation('CONFIGURE_WHEN_SHIPPING_COST_INCLUDED', pageScope)}
								note={getTranslation('CONFIGURE_WHEN_SHIPPING_COST_INCLUDED_DESCRIPTION', pageScope)}
								dataName="configure-when-shipping-cost-included"
							>
								<Table
									columns={[
										{
											content: getTranslation('REFUND_TO', pageScope),
											width: {
												value: 1,
												type: 'fr'
											}
										},
										{
											content: getTranslation('MANUAL_REFUND', pageScope),
											width: {
												value: 1,
												type: 'fr'
											}
										},
										{
											content: getTranslation('AUTOMATIC_REFUND', pageScope),
											width: {
												value: 1,
												type: 'fr'
											}
										},
									]}
									totalResultsTitle='Shipping Cost Setting'
								>
									{storeSetting?.shippingCostRefundSetting.map(refundSetting => (
										<TableRow
											key={REFUND_TYPES[refundSetting.refundType]}
											cells={[
												getTranslation(REFUND_TYPES[refundSetting.refundType], pageScope),
												<Toggle
													checked={getShippingCostRefundValue(refundSetting.isManualEnabled)}
													onChange={(v) => refundSetting.setIsManualEnabled(v)}
													label=''
													disabled={isShippingCostRefundTogglesDisabled()}
												/>,
												/* Auto-refund is not supported for bank account resolution for now */
												refundSetting.refundType !== REFUND_TYPES.BANK_REFUND && (
													<Toggle
														checked={getShippingCostRefundValue(refundSetting.isAutomaticEnabled)}
														onChange={(v) => refundSetting.setIsAutomaticEnabled(v)}
														label=''
														disabled={isShippingCostRefundTogglesDisabled()}
													/>
												)
											]}
										/>
									))}
								</Table>
							</FormFieldLine>
						</>

					)}



					<FormFieldLine
						label={getTranslation('CONFIGURE_REFUND_TO_BANK', pageScope)}
						note={getTranslation('CONFIGURE_REFUND_TO_BANK_DESCRIPTION', pageScope)}
						dataName="configure-refund-to-bank"
					>
						<Table
							columns={[
								{
									content: '',
									width: {
										value: 1,
										type: 'fr'
									}
								},
								{
									content: getTranslation('DISPLAY', pageScope),
									width: {
										value: 1,
										type: 'fr'
									}
								},
								{
									content: getTranslation('MANDATORY', pageScope),
									width: {
										value: 1,
										type: 'fr'
									}
								},
							]}
							totalResultsTitle='Shipping Cost Setting'
						>
							{storeSetting?.refundToBankSettingStore?.toggleTableData?.map(fieldSetting => (
								<TableRow
									key={fieldSetting.field}
									cells={[
										getTranslation(fieldSetting.translationKey, pageScope),
										<Toggle
											checked={fieldSetting.getShow()}
											onChange={fieldSetting.setShow}
											disabled={fieldSetting.showToggleDisabled()}
											label=''
										/>,
										<Toggle
											checked={fieldSetting.getRequire()}
											onChange={fieldSetting.setRequire}
											disabled={fieldSetting.requireToggleDisabled()}
											label=''
										/>,
									]}
								/>
							))}
						</Table>
					</FormFieldLine>

					<FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
						{getTranslation('REFUND_BONUS', pageScope)}
					</FontBody2>

					<FormFieldLine
						label={getTranslation('LIMIT_REFUND_BONUS', pageScope)}
						note={getTranslation('LIMIT_REFUND_BONUS_DESCRIPTION', pageScope)}
						dataName="limit-refund-bonus"
					>
						<Toggle
							checked={storeSetting?.rmaLevelBonusesLimitStore.isActive}
							onChange={onChangeIsRefundBonusActive}
							label=''
						/>
					</FormFieldLine>
					{storeSetting?.rmaLevelBonusesLimitStore.isActive && (
						<FormFieldLine
							label={getTranslation('MAXIMUM_REFUND_BONUS', pageScope)}
							note={getTranslation('MAXIMUM_REFUND_BONUS_DESCRIPTION', pageScope)}
							dataName="maximum-refund-bonus"
						>
							<div>
								<Input
									adornment={storeCurrency}
									value={storeSetting?.rmaLevelBonusesLimitStore.refundBonusesLimit ?? 0}
									onChange={onChangeRefundBonusesLimit}
									type="number"
									min={0}
									required
								/>
								<FontBody2 color={FONT_COLOR.NEUTRAL3}>
									{getTranslation('MAXIMUM_BONUS_PER_RMA', pageScope)}<FontBody2 color={FONT_COLOR.PRIMARY1}>{storeSetting?.rmaLevelBonusesLimitStore.refundBonusesLimit ?? 0} {storeCurrency}</FontBody2>
								</FontBody2>
							</div>
						</FormFieldLine>
					)}

					<FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
						{getTranslation('ADVANCED', pageScope)}
					</FontBody2>

					{isLineItemsRefundTransactionsSupported && (
						<FormFieldLine
							label={getTranslation('LINE_ITEMS_REFUND_TRANSACTIONS', pageScope)}
							note={getTranslation('LINE_ITEMS_REFUND_TRANSACTIONS_DESCRIPTION', pageScope)}
							dataName="line-items-refund-transactions"
						>
							<SelectDropdown
								value={lineItemRefundTransactionsValue}
								onChange={onChangeLineItemRefundTransactionsValue}
								options={[
									{
										label: getTranslation(LINE_ITEMS_REFUND_TRANSACTIONS_MAP[LINE_ITEMS_REFUND_TRANSACTIONS.ONE_TRANSACTION_PER_LINE_ITEM].label, pageScope),
										value: LINE_ITEMS_REFUND_TRANSACTIONS.ONE_TRANSACTION_PER_LINE_ITEM
									},
									{
										label: getTranslation(LINE_ITEMS_REFUND_TRANSACTIONS_MAP[LINE_ITEMS_REFUND_TRANSACTIONS.ONLY_ONE_TRANSACTION].label, pageScope),
										value: LINE_ITEMS_REFUND_TRANSACTIONS.ONLY_ONE_TRANSACTION
									},
								]}
							/>
						</FormFieldLine>
					)}


					<FormFieldLine
						label={getTranslation('ALWAYS_DISPLAY_AT_LEAST_ONE_REFUND_RESOLUTION', pageScope)}
						note={getTranslation('WHEN_ENABLED_CUSTOMERS_WILL_SEE_A_DISABLED_REFUND_RESOLUTION', pageScope)}
						dataName="always-display-at-least-one-refund-resolution"
					>
						<Toggle
							checked={storeSetting?.showDisabledResolution}
							onChange={storeSetting?.setShowDisabledResolution}
							label=''
						/>
					</FormFieldLine>
				</Card>
			)}
		</ResolveCapability>
    );
});

export default RefundsSettingsCard;
